import { useEffect, useState } from "react";
import { Check, Palette, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ACCENTS, useAccent } from "@/lib/accent";
import { api } from "@/lib/api";

const TRIGGER = {
  app: "flex h-10 items-center gap-2 border border-border px-3 text-muted-foreground transition-colors duration-200 hover:text-foreground",
  dark: "flex h-9 items-center gap-2 border border-zinc-800 px-3 text-zinc-500 transition-colors duration-200 hover:text-zinc-100",
};

export const AccentPicker = ({ variant = "app" }) => {
  const { accent, choose, followUnit, clearOwnChoice, ownChoice } = useAccent();
  const [unit, setUnit] = useState(null);

  useEffect(() => {
    if (variant !== "app") return;
    api
      .get("/settings/unit-colours")
      .then(({ data }) => {
        setUnit(data);
        followUnit(data.mine);
      })
      .catch(() => setUnit(null));
  }, [variant, followUnit]);

  const maySetUnit = (unit?.can_set || []).includes(unit?.my_unit);

  const setUnitColour = async (colour) => {
    try {
      await api.put(`/settings/unit-colours/${encodeURIComponent(unit.my_unit)}`, {
        accent: colour,
      });
      setUnit({ ...unit, mine: colour, accents: { ...unit.accents, [unit.my_unit]: colour } });
      toast.success(`${unit.my_unit} now carries ${ACCENTS[colour].label}`, {
        description: "Everyone in the unit sees this colour unless they pick their own.",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That colour could not be saved for the unit.");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="accent-picker"
          title="Change the portal colour"
          className={TRIGGER[variant] || TRIGGER.app}
        >
          <Palette className="h-4 w-4" />
          <span
            data-testid="accent-current"
            data-accent={accent}
            className="h-3 w-3 border border-black/20"
            style={{ background: ACCENTS[accent].swatch }}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-none">
        <DropdownMenuLabel className="font-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Portal colour
        </DropdownMenuLabel>
        {Object.entries(ACCENTS).map(([key, a]) => (
          <DropdownMenuItem
            key={key}
            data-testid={`accent-${key}`}
            onClick={() => choose(key)}
            className="gap-2 rounded-none font-data text-xs uppercase tracking-[0.14em]"
          >
            <span
              className="h-3 w-3 shrink-0 border border-black/20"
              style={{ background: a.swatch }}
            />
            {a.label}
            {accent === key && <Check className="ml-auto h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}

        {unit?.my_unit && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="font-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {unit.my_unit} carries {ACCENTS[unit.mine]?.label || unit.mine}
            </DropdownMenuLabel>
            {ownChoice && (
              <DropdownMenuItem
                data-testid="accent-use-unit"
                onClick={() => clearOwnChoice(unit.mine)}
                className="gap-2 rounded-none text-xs"
              >
                <RotateCcw className="h-3.5 w-3.5 shrink-0" />
                Use my unit's colour
              </DropdownMenuItem>
            )}
            {maySetUnit && (
              <DropdownMenuItem
                data-testid="accent-set-unit"
                onClick={() => setUnitColour(accent)}
                className="gap-2 rounded-none text-xs"
                disabled={unit.mine === accent}
              >
                <span
                  className="h-3 w-3 shrink-0 border border-black/20"
                  style={{ background: ACCENTS[accent].swatch }}
                />
                Set {unit.my_unit} to {ACCENTS[accent].label}
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
